import i18next from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import common from './locales/de/common.json';
import auditLog from './locales/de/audit-log.json';
import operatorSupport from './locales/de/operator-support.json';
import knowledge from './locales/de/knowledge.json';
import media from './locales/de/media.json';
import mediaBulk from './locales/de/media-bulk.json';
import mediaLibrary from './locales/de/media-library.json';
import mediaLicense from './locales/de/media-license.json';
import mediaSocialImport from './locales/de/media-social-import.json';
import mediaTrash from './locales/de/media-trash.json';
import knowledgeQualityRules from './locales/de/knowledge-quality-rules.json';
import knowledgeWritingStyle from './locales/de/knowledge-writing-style.json';
import voice from './locales/de/voice.json';
import voiceStyle from './locales/de/voice-style.json';
import voiceLessons from './locales/de/voice-lessons.json';
import voiceFeed from './locales/de/voice-feed.json';
import voiceVisual from './locales/de/voice-visual.json';
import knowledgeAddress from './locales/de/knowledge-address.json';
import knowledgeEntries from './locales/de/knowledge-entries.json';
import websiteImport from './locales/de/website-import.json';
import campaigns from './locales/de/campaigns.json';
import content from './locales/de/content.json';
import dataTable from './locales/de/data-table.json';
import contentHistory from './locales/de/content-history.json';
import contentVariants from './locales/de/content-variants.json';
import contentDelete from './locales/de/content-delete.json';
import contentRename from './locales/de/content-rename.json';
import contentTags from './locales/de/content-tags.json';
import contentMentions from './locales/de/content-mentions.json';
import contentWorkspace from './locales/de/content-workspace.json';
import brand from './locales/de/brand.json';
import calendar from './locales/de/calendar.json';
import review from './locales/de/review.json';
import designRenders from './locales/de/design-renders.json';
import designEditor from './locales/de/design-editor.json';
import notifications from './locales/de/notifications.json';
import ai from './locales/de/ai.json';
import aiLanguages from './locales/de/ai-languages.json';
import search from './locales/de/search.json';
import legal from './locales/de/legal.json';
import landing from './locales/de/landing.json';
import signUp from './locales/de/sign-up.json';
import accounts from './locales/de/accounts.json';
import help from './locales/de/help.json';
import charts from './locales/de/charts.json';
import inbox from './locales/de/inbox.json';
import publications from './locales/de/publications.json';
import analytics from './locales/de/analytics.json';
import dashboard from './locales/de/dashboard.json';
import reels from './locales/de/reels.json';
import planning from './locales/de/planning.json';
import setup from './locales/de/setup.json';
import newPost from './locales/de/new-post.json';
import photoPost from './locales/de/photo-post.json';
import preview from './locales/de/preview.json';
import settings from './locales/de/settings.json';
import reviewQuick from './locales/de/review-quick.json';
import shell from './locales/de/shell.json';
import billing from './locales/de/billing.json';
import occasionStandby from './locales/de/occasion-standby.json';
import integrations from './locales/de/integrations.json';
import feedback from './locales/de/feedback.json';
import assistant from './locales/de/assistant.json';

/**
 * Translation layer (REQ-05-018): source locale `de`, ICU message syntax
 * (`{version}`, plurals). Every display text comes from `locales/de/*.json`.
 */
export const SOURCE_LOCALE = 'de';

export type Catalog = { readonly [key: string]: string | Catalog };

/**
 * Module catalogs (`locales/de/<module>.json`) nest under the same top-level
 * keys as `common.json` (e.g. `knowledge.entries.*`) and are deep-merged
 * into the one `common` namespace, so keys stay `t('knowledge.entries.intro')`
 * while no single locale file outgrows the file-size limit.
 */
export function mergeCatalogs(...catalogs: Catalog[]): Catalog {
  const merged: Record<string, string | Catalog> = {};
  for (const catalog of catalogs) {
    for (const [key, value] of Object.entries(catalog)) {
      const existing = merged[key];
      merged[key] = typeof value === 'object' && typeof existing === 'object' ? mergeCatalogs(existing, value) : value;
    }
  }
  return merged;
}

export const resources = {
  de: {
    common: mergeCatalogs(
      common,
      auditLog,
      operatorSupport,
      dataTable,
      knowledge,
      knowledgeQualityRules,
      knowledgeWritingStyle,
      voice,
      voiceStyle,
      voiceLessons,
      voiceFeed,
      voiceVisual,
      knowledgeAddress,
      knowledgeEntries,
      websiteImport,
      media,
      mediaLibrary,
      mediaBulk,
      mediaLicense,
      mediaSocialImport,
      mediaTrash,
      content,
      contentVariants,
      contentHistory,
      contentDelete,
      contentRename,
      contentTags,
      contentMentions,
      contentWorkspace,
      campaigns,
      brand,
      calendar,
      review,
      designRenders,
      designEditor,
      notifications,
      ai,
      aiLanguages,
      search,
      legal,
      landing,
      signUp,
      accounts,
      inbox,
      help,
      charts,
      publications,
      dashboard,
      analytics,
      reels,
      planning,
      setup,
      newPost,
      photoPost,
      preview,
      reviewQuick,
      shell,
      settings,
      billing,
      occasionStandby,
      integrations,
      feedback,
      assistant,
    ),
  },
} as const;

export function initI18n(): typeof i18next {
  if (!i18next.isInitialized) {
    void i18next
      .use(ICU)
      .use(initReactI18next)
      .init({
        lng: SOURCE_LOCALE,
        fallbackLng: SOURCE_LOCALE,
        defaultNS: 'common',
        ns: ['common'],
        resources,
        initAsync: false,
        interpolation: { escapeValue: false },
        // The ICU plugin caches compiled messages; drop them when a bundle is replaced.
        i18nFormat: { bindI18nStore: 'added' },
        returnNull: false,
      });
  }
  return i18next;
}
